import DraftBadge, { useDraftSummaries } from '../DraftBadge';
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../ThreadQuill.css';
import { fetchConversations, openOrSendMessage } from '../store/conversationsSlice';
import {
  fetchAdminConversations,
  fetchCoaches,
  fetchAssistants,
  fetchPickerPlayers,
  createAdminConversation,
} from '../store/adminConversationsSlice';

const stripHtml = html => (html || '').replace(/<[^>]*>/g, '').trim();
const isQuillEmpty = val => !val || val.replace(/<[^>]*>/g, '').trim() === '';

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const quillFormats = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

const CoachInboxPage = () => {
  const draftSummaries = useDraftSummaries();
  const dispatch = useDispatch();
  const history = useHistory();
  const { list: playerConversations, loading } = useSelector(
    ({ conversations: convState }) => convState
  );
  const {
    list: adminConversations,
    loading: adminLoading,
    coaches,
    assistants,
    players,
  } = useSelector(({ adminConversations: adminState }) => adminState);
  const userRole = useSelector(({ authorization }) => authorization.userRole);

  const isAdmin = userRole === 'admin';
  const isCoach = userRole === 'coach';
  const isAssistant = userRole === 'assistant';

  // New message modal state
  const [showNewMsg, setShowNewMsg] = useState(false);
  const [recipientType, setRecipientType] = useState(null); // 'coach' | 'assistant' | 'player'
  const [search, setSearch] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const pickerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    dispatch(fetchConversations());
    if (isCoach || isAdmin || isAssistant) {
      dispatch(fetchAdminConversations());
    }
  }, [dispatch, userRole, isCoach, isAdmin, isAssistant]);

  // Fetch coaches/players when admin selects recipient type
  useEffect(() => {
    if (!isAdmin || !recipientType) return;
    if (recipientType === 'coach' && coaches.length === 0) {
      dispatch(fetchCoaches());
    }
    if (recipientType === 'assistant' && assistants.length === 0) {
      dispatch(fetchAssistants());
    }
    if (recipientType === 'player' && players.length === 0) {
      dispatch(fetchPickerPlayers());
    }
  }, [dispatch, isAdmin, recipientType, coaches.length, assistants.length, players.length]);

  const resetModal = () => {
    setShowNewMsg(false);
    setRecipientType(null);
    setSearch('');
    setSelectedRecipient(null);
    setMsgText('');
    setSelectedFile(null);
  };

  const handleFileChange = useCallback(e => {
    const file = e.target.files[0] || null;
    if (file && file.size > 10 * 1024 * 1024) {
      // eslint-disable-next-line no-alert
      alert('Plik musi być mniejszy niż 10 MB');
      // eslint-disable-next-line no-param-reassign
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    // eslint-disable-next-line no-param-reassign
    e.target.value = '';
  }, []);

  const pickerList = recipientType === 'coach'
    ? coaches
    : recipientType === 'assistant'
      ? assistants
      : players;

  const filteredList = useMemo(() => {
    const valid = pickerList.filter(c => (c.firstName || '').trim() || (c.lastName || '').trim());
    if (!search.trim()) return valid;
    const q = search.toLowerCase();
    return valid.filter(c =>
      (c.firstName || '').toLowerCase().includes(q) ||
      (c.lastName || '').toLowerCase().includes(q) ||
      (c.schoolName || '').toLowerCase().includes(q) ||
      (c.sport || '').toLowerCase().includes(q) ||
      (c.currentClub || '').toLowerCase().includes(q)
    );
  }, [pickerList, search]);

  const handleSend = async () => {
    if (!selectedRecipient || (isQuillEmpty(msgText) && !selectedFile)) return;
    setSending(true);
    let result;
    if (recipientType === 'coach' || recipientType === 'assistant') {
      result = await dispatch(
        createAdminConversation({ coachUserId: selectedRecipient._id, text: msgText, file: selectedFile })
      );
      setSending(false);
      if (result) {
        const convId = result.conversationId;
        resetModal();
        setTimeout(() => history.push(`/admin/coach-messages/${convId}`), 0);
      }
    } else {
      result = await dispatch(
        openOrSendMessage({ playerId: selectedRecipient._id, text: msgText, file: selectedFile })
      );
      setSending(false);
      if (result) {
        const convId = result.conversationId;
        resetModal();
        setTimeout(() => history.push(`/messages/${convId}`), 0);
      }
    }
  };

  // Merge player + admin conversations, sorted by lastMessageAt
  const allConversations = useMemo(() => {
    const tagged = [
      ...playerConversations.map(c => ({ ...c, _type: 'player' })),
      ...((isCoach || isAdmin || isAssistant)
        ? adminConversations.map(c => ({ ...c, _type: 'admin' }))
        : []),
    ];
    tagged.sort((a, b) => {
      const aTime = a.lastMessage?.createdAt || a.lastMessageAt || 0;
      const bTime = b.lastMessage?.createdAt || b.lastMessageAt || 0;
      return new Date(bTime) - new Date(aTime);
    });
    return tagged;
  }, [playerConversations, adminConversations, isCoach, isAdmin]);

  const isInitialLoad = loading && playerConversations.length === 0
    && (!((isCoach || isAdmin || isAssistant)) || (adminLoading && adminConversations.length === 0));
  if (isInitialLoad) {
    return (
      <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
        {isAdmin ? 'Ładowanie wiadomości...' : 'Loading messages...'}
      </div>
    );
  }

  const labels = isAdmin
    ? {
        title: 'Wiadomości',
        newMessage: 'Nowa wiadomość',
        noConversations: 'Brak konwersacji.',
        noConversationsHint: 'Kliknij "Nowa wiadomość", aby rozpocząć rozmowę.',
        modalTitle: 'Nowa wiadomość',
        chooseRecipient: 'Wybierz typ odbiorcy',
        coach: 'Trener',
        assistant: 'Asystentka',
        player: 'Zawodnik',
        searchCoach: 'Szukaj trenera po nazwisku lub szkole...',
        searchAssistant: 'Szukaj asystentki po nazwisku...',
        searchPlayer: 'Szukaj zawodnika po nazwisku lub sporcie...',
        loading: 'Ładowanie...',
        notFound: 'Nie znaleziono',
        to: 'Do',
        cancel: 'Anuluj',
        send: 'Wyślij',
        back: 'Wstecz',
        messagePlaceholder: 'Wpisz wiadomość...',
        you: 'Ty',
        admin: 'Kajetan Borecki',
      }
    : {
        title: 'Messages',
        noConversations: 'No conversations yet.',
        noConversationsHint: 'Start a conversation by clicking "Message" on a player\'s profile.',
        you: 'You',
        admin: 'Kajetan Borecki',
      };

  return (
    <div style={{ padding: '32px 0', minHeight: 'calc(100vh - 160px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
          {labels.title}
        </h1>
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowNewMsg(true)}
            style={{
              padding: '8px 18px',
              background: '#2e496b',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {labels.newMessage}
          </button>
        )}
      </div>

      {/* New message modal (admin only) */}
      {showNewMsg && isAdmin && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          onClick={e => { if (e.target === e.currentTarget) resetModal(); }}
          role="presentation"
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '12px',
              padding: '24px',
              width: '520px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ margin: 0, fontSize: '18px', color: '#1e293b' }}>{labels.modalTitle}</h2>
              <button
                type="button"
                onClick={resetModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#94a3b8' }}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            {/* Step 1: Choose recipient type */}
            {!recipientType && !selectedRecipient && (
              <div>
                <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#64748b' }}>{labels.chooseRecipient}</p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setRecipientType('coach')}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#f8fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1e293b',
                      textAlign: 'center',
                    }}
                  >
                    <i className="fas fa-chalkboard-teacher" style={{ display: 'block', fontSize: '24px', marginBottom: '8px', color: '#2e496b' }} />
                    {labels.coach}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecipientType('assistant')}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#f8fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1e293b',
                      textAlign: 'center',
                    }}
                  >
                    <i className="fas fa-user-shield" style={{ display: 'block', fontSize: '24px', marginBottom: '8px', color: '#2e496b' }} />
                    {labels.assistant}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRecipientType('player')}
                    style={{
                      flex: 1,
                      padding: '16px',
                      background: '#f8fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '600',
                      color: '#1e293b',
                      textAlign: 'center',
                    }}
                  >
                    <i className="fas fa-running" style={{ display: 'block', fontSize: '24px', marginBottom: '8px', color: '#2e496b' }} />
                    {labels.player}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Pick recipient from list */}
            {recipientType && !selectedRecipient && (
              <div ref={pickerRef}>
                <button
                  type="button"
                  onClick={() => { setRecipientType(null); setSearch(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#2e496b', fontSize: '13px', padding: 0, marginBottom: '12px' }}
                >
                  &#8592; {labels.back}
                </button>
                <input
                  type="text"
                  placeholder={
                    recipientType === 'coach'
                      ? labels.searchCoach
                      : recipientType === 'assistant'
                        ? labels.searchAssistant
                        : labels.searchPlayer
                  }
                  value={search}
                  onChange={e => { const v = e.target.value; setSearch(v); }}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    marginBottom: '8px',
                  }}
                />
                <div style={{
                  maxHeight: '260px',
                  overflowY: 'auto',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}>
                  {filteredList.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#94a3b8', fontSize: '14px', padding: '12px' }}>
                      {pickerList.length === 0 ? labels.loading : labels.notFound}
                    </p>
                  )}
                  {filteredList.map(c => (
                    <button
                      key={c._id}
                      type="button"
                      onClick={() => setSelectedRecipient(c)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 12px',
                        border: 'none',
                        borderBottom: '1px solid #f1f5f9',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      <span style={{ fontWeight: '600', color: '#1e293b' }}>
                        {c.firstName} {c.lastName}
                      </span>
                      {recipientType === 'coach' && c.schoolName && (
                        <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '13px' }}>
                          {c.schoolName}
                        </span>
                      )}
                      {recipientType === 'assistant' && c.schoolName && (
                        <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '13px' }}>
                          {c.schoolName}
                        </span>
                      )}
                      {recipientType === 'player' && c.sport && (
                        <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '13px' }}>
                          {c.sport}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Compose message */}
            {selectedRecipient && (
              <>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '16px',
                  padding: '8px 12px',
                  background: '#f1f5f9',
                  borderRadius: '8px',
                }}>
                  <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '14px' }}>
                    {labels.to}: {selectedRecipient.firstName} {selectedRecipient.lastName}
                  </span>
                  {(recipientType === 'coach' || recipientType === 'assistant') && selectedRecipient.schoolName && (
                    <span style={{ color: '#64748b', fontSize: '13px' }}>({selectedRecipient.schoolName})</span>
                  )}
                  {recipientType === 'player' && selectedRecipient.sport && (
                    <span style={{ color: '#64748b', fontSize: '13px' }}>({selectedRecipient.sport})</span>
                  )}
                  <button
                    type="button"
                    onClick={() => setSelectedRecipient(null)}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
                <div className="thread-quill" style={{ marginBottom: '12px' }}>
                  <ReactQuill
                    value={msgText}
                    onChange={setMsgText}
                    modules={quillModules}
                    formats={quillFormats}
                    theme="snow"
                    placeholder={labels.messagePlaceholder}
                  />
                </div>
                {selectedFile && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '12px', color: '#64748b' }}>
                    <i className="fas fa-paperclip" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' }}>
                      {selectedFile.name}
                    </span>
                    <span style={{ opacity: 0.6 }}>({(selectedFile.size / 1024).toFixed(0)} KB)</span>
                    <button
                      type="button"
                      onClick={() => setSelectedFile(null)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#e53e3e', padding: '0 2px', lineHeight: 1 }}
                      title="Usuń załącznik"
                    >
                      <i className="fas fa-times" />
                    </button>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    title="Załącz plik (max 10 MB)"
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: selectedFile ? '#2e496b' : '#94a3b8',
                      padding: '4px',
                      fontSize: '16px',
                    }}
                  >
                    <i className="fas fa-paperclip" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={resetModal}
                      style={{
                        padding: '8px 16px',
                        background: 'transparent',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: 'pointer',
                        color: '#64748b',
                      }}
                    >
                      {labels.cancel}
                    </button>
                    <button
                      type="button"
                      onClick={handleSend}
                      disabled={sending || (isQuillEmpty(msgText) && !selectedFile)}
                      style={{
                        padding: '8px 18px',
                        background: '#2e496b',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: sending || (isQuillEmpty(msgText) && !selectedFile) ? 'not-allowed' : 'pointer',
                        opacity: sending || (isQuillEmpty(msgText) && !selectedFile) ? 0.6 : 1,
                      }}
                    >
                      {labels.send}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Conversation list */}
      {allConversations.length === 0 ? (
        <div
          style={{
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '40px',
            textAlign: 'center',
            color: '#64748b',
          }}
        >
          <p style={{ fontSize: '16px', margin: 0 }}>{labels.noConversations}</p>
          <p style={{ fontSize: '14px', marginTop: '8px' }}>{labels.noConversationsHint}</p>
        </div>
      ) : (
        <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
          {allConversations.map(conv => {
            const isAdminConv = conv._type === 'admin';

            let displayName;
            let onClick;
            let senderPrefix;

            if (isAdminConv) {
              if (isAdmin) {
                const coach = conv.coachUserId;
                const counterpartRole = coach?.role === 'assistant' ? 'assistant' : 'coach';
                displayName = coach
                  ? `${coach.firstName} ${coach.lastName}`
                  : counterpartRole === 'assistant' ? 'Asystentka' : 'Trener';
                onClick = () => history.push(`/admin/coach-messages/${conv._id}`);
                const lastMsg = conv.lastMessage;
                senderPrefix = lastMsg
                  ? lastMsg.senderType === 'admin' ? `${labels.you}: ` : `${displayName.split(' ')[0]}: `
                  : '';
              } else {
                displayName = 'Kajetan Borecki';
                onClick = () => history.push(`/messages/admin/${conv._id}`);
                const lastMsg = conv.lastMessage;
                senderPrefix = lastMsg
                  ? lastMsg.senderType === 'coach' ? `${labels.you}: ` : `${labels.admin}: `
                  : '';
              }
            } else {
              const player = conv.playerProfileId;
              displayName = player
                ? `${player.firstName} ${player.lastName}`
                : isAdmin ? 'Nieznany zawodnik' : 'Unknown Player';
              onClick = () => history.push(`/messages/${conv._id}`);
              const lastMsg = conv.lastMessage;
              senderPrefix = lastMsg
                ? lastMsg.senderType === 'coach'
                  ? `${labels.you}: `
                  : `${player?.firstName || (isAdmin ? 'Zawodnik' : 'Player')}: `
                : '';
            }

            const lastMsg = conv.lastMessage;
            const unread = conv.unreadCount || 0;
            const isUnread = unread > 0 || (isAdminConv && conv.markedUnreadByAdmin) || (!isAdminConv && conv.markedUnreadByCoach);

            return (
              <button
                key={`${conv._type}-${conv._id}`}
                type="button"
                onClick={onClick}
                style={{
                  background: isUnread ? '#e8f0fe' : '#ffffff',
                  border: 'none',
                  borderBottom: '1px solid #f1f5f9',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                {/* Unread dot */}
                <div style={{ width: '8px', flexShrink: 0 }}>
                  {isUnread && (
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#2e496b' }} />
                  )}
                </div>
                {/* Sender name */}
                <div style={{ width: '180px', flexShrink: 1, minWidth: '80px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ fontWeight: isUnread ? '700' : '500', fontSize: '14px', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {displayName}
                  </span>
                  {isAdminConv && (
                    <span style={{
                      fontSize: '10px',
                      color: '#2e496b',
                      background: '#e8edf3',
                      borderRadius: '4px',
                      padding: '1px 6px',
                      fontWeight: '600',
                      flexShrink: 0,
                    }}>
                      {isAdmin
                        ? (conv.coachUserId?.role === 'assistant' ? 'ASYSTENT' : 'TRENER')
                        : 'ADMIN'}
                    </span>
                  )}
                </div>
                {/* Preview text */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <DraftBadge summaries={draftSummaries} threadType={isAdminConv ? 'admin-conversations' : 'conversations'} conversationId={conv._id} />
                  {lastMsg && (
                    <span
                      style={{
                        fontSize: '13px',
                        color: isUnread ? '#334155' : '#94a3b8',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        fontWeight: isUnread ? '600' : '400',
                      }}
                    >
                      {senderPrefix}
                      {stripHtml(lastMsg.text)}
                    </span>
                  )}
                </div>
                {/* Date + badge */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  {lastMsg && (
                    <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                      {new Date(lastMsg.createdAt).toLocaleDateString([], {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  )}
                  {isUnread && (
                    <span
                      style={{
                        background: '#2e496b',
                        color: '#ffffff',
                        borderRadius: '12px',
                        padding: '2px 8px',
                        fontSize: '11px',
                        fontWeight: '700',
                        minWidth: '20px',
                        textAlign: 'center',
                      }}
                    >
                      {unread > 0 ? unread : '●'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CoachInboxPage;
